import { Link } from 'react-router-dom';
import { MarketingShell } from '../components/MarketingShell';

// Public plain-English AI policy page. Parents click "AI Policy" from the
// marketing footer or the consent email to read this. Deliberately written for
// a non-technical parent, not a compliance officer.
export function AiPolicyScreen() {
  return (
    <MarketingShell>
      <main className="max-w-3xl mx-auto px-6 py-12">
        <p className="uppercase tracking-[0.18em] text-xs text-accent font-semibold">AI Policy</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-ink">
          What our AI does, and what it doesn&apos;t
        </h1>
        <p className="mt-3 text-sm text-ink/55">Last updated: 28 July 2026</p>

        <p className="mt-6 text-base text-ink/80 leading-relaxed">
          My Dyslexia Tools uses Artificial Intelligence to help children with dyslexia complete their
          homework. This page explains, in plain English, what the AI does with your child&apos;s photos,
          answers, and voice - and what it never does.
        </p>

        <Section title="What the AI is for">
          <p>Four small jobs, one at a time, on the worksheet in front of your child:</p>
          <ul className="list-disc pl-6 mt-3 space-y-1.5">
            <li><strong>Read the worksheet.</strong> Look at a photo your child took, find each question, work out where the answer goes on the page.</li>
            <li><strong>Explain a question.</strong> If your child taps &quot;explain this&quot;, rephrase a hard question in simpler words.</li>
            <li><strong>Tidy up dictation.</strong> If your child dictates an answer and speech-to-text mishears a place name or word from the worksheet, restore the correct spelling.</li>
            <li><strong>Predict the next word.</strong> Suggest the next word or two while your child is typing an answer.</li>
          </ul>
        </Section>

        <Section title="What the AI is NOT for">
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Answering homework questions FOR your child. We deliberately do not do this.</li>
            <li>Giving your child personal advice, life coaching, or opinions on anything outside their worksheet.</li>
            <li>Discussing anything sexual, violent, or illegal. We block that at the door and it never reaches the AI.</li>
            <li>Being a general chatbot. If your child tries to have a conversation about anything else, we redirect them back to the worksheet.</li>
          </ul>
        </Section>

        <Section title="What happens to my child&apos;s photo">
          <ul className="list-disc pl-6 space-y-1.5">
            <li>The photo goes from your child&apos;s device straight to the AI provider (Anthropic) to be read.</li>
            <li>The AI provider does not keep the photo. It is processed once and discarded.</li>
            <li>We do not keep the photo on our servers. The photo lives only on your child&apos;s device unless they turn on cloud sync in Settings.</li>
            <li>We do not train AI models on your child&apos;s photos, answers, or voice. Ever. It is written into our contract with the AI provider.</li>
          </ul>
        </Section>

        <Section title="Which AI provider">
          <p>
            We use <strong>Anthropic&apos;s Claude</strong> for reading worksheets, explaining questions, tidying
            dictation, and predicting words. We use <strong>ElevenLabs</strong> for premium voice
            (subscribers only - free users get the built-in browser voice, so no data leaves the device).
          </p>
          <p className="mt-3">
            Both providers are UK/EU-compliant, have Data Processing Agreements with us, and confirm in
            writing that they do not train on our users&apos; data.
          </p>
        </Section>

        <Section title="What we do to keep your child safe">
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Age gate.</strong> We ask for date of birth on sign-up. Under-13s need a parent to click a permission link before the app unlocks. (UK GDPR Article 8.)</li>
            <li><strong>Photo safeguarding.</strong> On-device checks reject photos of faces, bodies, or blank pages before they ever reach the AI.</li>
            <li><strong>Blocked words.</strong> A filter catches sexual, illegal, or manipulation attempts before they reach the AI.</li>
            <li><strong>Crisis signposting.</strong> Named UK helplines - Childline, NSPCC, YoungMinds - are one tap away throughout the app.</li>
            <li><strong>No conversation.</strong> The AI never chats. It answers one specific request per interaction and stops.</li>
            <li><strong>No advertising.</strong> We do not show ads. We do not sell data. Ever.</li>
          </ul>
        </Section>

        <Section title="How long we keep things">
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Photos of worksheets:</strong> not kept. Processed and discarded on the AI provider&apos;s side; not stored on our servers.</li>
            <li><strong>Finished worksheets (with your child&apos;s answers):</strong> saved on your child&apos;s device by default. Only synced to our servers if you turn on cloud sync in Settings.</li>
            <li><strong>Account details (email, date of birth, subscription status):</strong> kept for as long as the account is active. Deleted within 30 days of you closing the account.</li>
            <li><strong>Parental consent record:</strong> kept for 6 years as a legal audit trail, then deleted. Required by UK data protection law.</li>
          </ul>
        </Section>

        <Section title="Getting your child&apos;s data out or deleted">
          <p>
            You can delete your child&apos;s account and everything we hold about them at any time from Settings
            in the app, or by emailing <a href="mailto:support@mydyslexiatools.com" className="underline">support@mydyslexiatools.com</a>.
            We&apos;ll confirm within 24 hours and delete within 30 days (usually within a few hours).
          </p>
          <p className="mt-3">
            If you want a copy of your child&apos;s data (a Subject Access Request under UK GDPR), email us at
            the same address. We&apos;ll send it within 30 days.
          </p>
        </Section>

        <Section title="Withdrawing parental consent">
          <p>
            If you gave consent for your under-13 child and want to withdraw it, email us at{' '}
            <a href="mailto:support@mydyslexiatools.com" className="underline">support@mydyslexiatools.com</a>.
            Your child&apos;s account will be locked immediately and their data deleted within 30 days,
            same as a normal account closure.
          </p>
        </Section>

        <p className="mt-10 text-sm text-ink/60">
          Related reading:{' '}
          <Link to="/privacy" className="underline">Privacy Policy</Link> &middot;{' '}
          <Link to="/terms" className="underline">Terms of Service</Link> &middot;{' '}
          <Link to="/support" className="underline">Support</Link>
        </p>
      </main>
    </MarketingShell>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-xl font-semibold tracking-tight text-ink">{title}</h2>
      <div className="mt-3 text-base text-ink/80 leading-relaxed space-y-2">{children}</div>
    </section>
  );
}
